import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";

export type DuplicateCandidateStatus =
  | "open"
  | "confirmed_duplicate"
  | "merged"
  | "not_a_duplicate";

export type DuplicateReasonCode =
  | "kundennummer_match"
  | "vertragsnummer_match"
  | "firma_email"
  | "firma_address"
  | "firma_phone"
  | "firma_name"
  | "firma_person"
  | "name_birthdate"
  | "name_only"
  | "name_address"
  | "lastname_email"
  | "shared_email"
  | "shared_phone";

export interface DuplicateCandidate {
  id: number;
  tenantId: string;
  leftContactId: number;
  rightContactId: number;
  reasons: DuplicateReasonCode[];
  score: number;
  status: DuplicateCandidateStatus;
  createdAt: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
}

export interface AnsprechpartnerLink {
  companyId: number;
  companyName: string;
  function?: string | null;
}

export interface RelatedSummary {
  addresses: number;
  communications: number;
  bankAccounts: number;
  relations: number;
  vorgaenge: number;
  documents: number;
  damages: number;
  contracts: number;
  risks: number;
  ansprechpartnerOf: AnsprechpartnerLink[];
}

export interface ClusterContactSnapshot {
  id: number;
  lastname?: string;
  firstName?: string;
  displayName?: string;
  contactNbrInt?: string | null;
  contactNbrExt?: string | null;
  type?: string | null;
  defaultAddress?: {
    street?: string;
    postalCode?: string;
    city?: string;
  } | null;
  defaultEmail?: string | null;
  defaultPhone?: string | null;
  /** Kontaktverantwortlicher: assigned BO team (read-only badge + Alle/Team filter). */
  boTeam?: { id: string; name: string | null } | null;
  counts?: RelatedSummary;
  /** Already merged away (hard-deleted loser) — render struck-through/greyed. */
  isDeleted?: boolean;
}

export interface DuplicateClusterPair {
  id: number;
  leftContactId: number;
  rightContactId: number;
  reasons: DuplicateReasonCode[];
  score: number;
  status: DuplicateCandidateStatus;
}

export interface DuplicateCluster {
  clusterId: number;
  score: number;
  contactIds: number[];
  reasons: DuplicateReasonCode[];
  pairs: DuplicateClusterPair[];
  contacts: ClusterContactSnapshot[];
  lockedBy?: string | null;
  lockedByName?: string | null;
  lockedAt?: string | null;
  /** Lock held by the current user. */
  lockedByMe?: boolean;
  /** Lock held by someone else → render disabled. */
  isLocked?: boolean;
}

const readErrorMessage = async (
  res: Response,
  fallback: string,
): Promise<string> => {
  try {
    const d = await res.json();
    if (d?.message)
      return Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
  } catch {
    /* ignore */
  }
  return fallback;
};

export interface RelatedDetailItem {
  id: number;
  label: string;
  sub?: string | null;
  isDefault?: boolean;
  /** Communication channel: phone | mobile | email | fax | … (comms only). */
  kind?: string | null;
  /** privat / geschäftlich split for communications. */
  category?: "privat" | "geschäftlich" | null;
  /** Raw value (e.g. the phone number / e-mail). */
  value?: string | null;
  /** Für Beziehungen: Typ des Gegenübers ('Person' | 'Firma') — trennt Beziehungen/Verbindungen. */
  otherType?: string | null;
  /**
   * Ist das Gegenüber wirklich ein Firmen-Kontakt? `otherType` meldet auch bei
   * extern verknüpften Ansprechpartnern „Firma", obwohl dort eine Person steht —
   * für Aussagen über den echten Typ (Firma↔Person-Merge) taugt es deshalb nicht.
   */
  otherIsCompanyContact?: boolean;
  /** Beziehungen: gespeicherter Beziehungstyp (z. B. „Geschäftsführer"). */
  relationType?: string | null;
  /** Beziehungen: Kontakt-ID des Gegenübers. */
  otherContactId?: number | null;
  /** Beziehungen: Kontaktnummer des Gegenübers (intern, sonst extern). */
  otherContactNbr?: string | null;
  /** Beziehungen: Gegenüber steckt selbst in einem offenen Duplikat-Cluster. */
  inDuplicateCluster?: boolean;
  // Rohfelder fürs Bearbeiten (Sieger-Datensätze im Merge):
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  ortsteil?: string | null;
  addressDescription?: string | null;
  bankName?: string | null;
  iban?: string | null;
  bic?: string | null;
  accountHolder?: string | null;
  bankDescription?: string | null;
}

export interface RelatedDetail {
  addresses: RelatedDetailItem[];
  communications: RelatedDetailItem[];
  bankAccounts: RelatedDetailItem[];
  relations: RelatedDetailItem[];
  kundenverbindungen: RelatedDetailItem[];
  vorgaenge: RelatedDetailItem[];
  documents: RelatedDetailItem[];
  damages: RelatedDetailItem[];
  contracts: RelatedDetailItem[];
  risks: RelatedDetailItem[];
}

export const formatReasonCode = (code: DuplicateReasonCode): string => {
  switch (code) {
    case "kundennummer_match":
      return "Gleiche Kundennummer";
    case "vertragsnummer_match":
      return "Gleiche Vertragsnummer";
    case "name_birthdate":
      return "Vorname + Nachname + Geburtsdatum";
    case "name_only":
      return "Vorname + Nachname";
    case "lastname_email":
      return "Nachname + E-Mail";
    case "firma_email":
      return "Firmenname + E-Mail";
    case "firma_address":
      return "Firmenname + Anschrift";
    case "firma_phone":
      return "Firmenname + Telefon";
    case "firma_name":
      return "Gleicher Firmenname";
    case "firma_person":
      return "Firma ↔ Person (Zieltyp wählen)";
    case "name_address":
      return "Nachname + Anschrift";
    case "shared_email":
      return "Gleiche E-Mail";
    case "shared_phone":
      return "Gleiche Telefonnummer";
    default:
      return String(code);
  }
};

/** Paginated cluster list. Returns the page plus the total CLUSTER count. */
export const listDuplicateClusters = async (
  status: DuplicateCandidateStatus | "all",
  limit: number,
  offset: number,
  q?: string,
  signal?: AbortSignal,
  teamId?: string,
): Promise<{ clusters: DuplicateCluster[]; total: number }> => {
  const { data, total } = await apiFetch<DuplicateCluster[]>(
    "duplicate-candidates",
    {
      params: {
        status,
        limit,
        offset,
        ...(q ? { q } : {}),
        ...(teamId ? { teamId } : {}),
      },
      signal,
    },
  );
  return { clusters: data ?? [], total: total ?? (data?.length ?? 0) };
};

/** BO teams for the Übersicht „Verantwortlich"-Filter (Alle/Team). */
export const listDuplicateTeams = async (): Promise<
  Array<{ id: string; name: string | null }>
> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "duplicate-candidates/teams",
  );
  return data ?? [];
};

export const refreshDuplicateCandidates = async (): Promise<{
  created: number;
  total: number;
}> => {
  const res = await fetch(apiUrl("duplicate-candidates/refresh"), {
    method: "POST",
  });
  if (!res.ok) throw new Error(`Refresh duplicates failed: HTTP ${res.status}`);
  return res.json();
};

export const updateDuplicateCandidateStatus = async (
  id: number,
  status: DuplicateCandidateStatus,
): Promise<DuplicateCandidate> => {
  const res = await fetch(apiUrl(`duplicate-candidates/${id}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`Update status failed: HTTP ${res.status}`);
  return res.json();
};

/** Legacy two-way merge (kept for the /contacts/merge?leftId&rightId URL). */
export const mergeContacts = async (
  winnerId: number,
  loserId: number,
): Promise<{ winnerId: number; loserId: number }> => {
  const res = await fetch(apiUrl("duplicate-candidates/merge"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ winnerId, loserId }),
  });
  if (!res.ok) throw new Error(`Merge contacts failed: HTTP ${res.status}`);
  return res.json();
};

export interface MergeFieldSelection {
  column: string;
  sourceContactId: number;
}

/** Per-category DROP-sets of child-record ids: only these are deleted from the
 *  survivor, everything else is kept (additive-safe). An omitted/empty category
 *  drops nothing. */
export interface MergeRecordSelections {
  addresses?: number[];
  communications?: number[];
  banks?: number[];
  relations?: number[];
  /** Verträge, die verworfen werden (Soft-Delete) — i.d.R. abgewählte Altkunde-Verträge. */
  contracts?: number[];
}

/** N-way merge: every loser folds into the survivor in one transaction. */
export const mergeCluster = async (
  survivorId: number,
  loserIds: number[],
  fieldSelections: MergeFieldSelection[] = [],
  recordSelections?: MergeRecordSelections,
  /** Optional BO-team to assign to the survivor (Kontaktverantwortlichkeit). */
  assignBoTeamId?: string,
  /** Optional FO-user to assign to the survivor (Frontoffice). */
  assignFoUserId?: string,
  /** Freie Sieger-Werte (vom User editiert): DB-Spalte → Wert. */
  explicitValues?: Array<{ column: string; value: string | number | null }>,
  /** Sieger-Datensätze (Adresse/Komm/Bank) bearbeiten/hinzufügen. */
  childrenModifications?: Record<
    string,
    { add?: unknown[]; update?: Array<{ id: number; data: unknown }> }
  >,
): Promise<{ survivorId: number; merged: number[] }> => {
  const res = await fetch(apiUrl("duplicate-candidates/merge-cluster"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      survivorId,
      loserIds,
      fieldSelections,
      ...(recordSelections ? { recordSelections } : {}),
      ...(assignBoTeamId ? { assignBoTeamId } : {}),
      ...(assignFoUserId ? { assignFoUserId } : {}),
      ...(explicitValues && explicitValues.length ? { explicitValues } : {}),
      ...(childrenModifications &&
      Object.keys(childrenModifications).length
        ? { childrenModifications }
        : {}),
    }),
  });
  if (!res.ok)
    throw new Error(
      await readErrorMessage(res, `Merge cluster failed: HTTP ${res.status}`),
    );
  return res.json();
};

/** Reines Altkunden-Cluster verwerfen (statt mergen): alle Kontakte in den
 *  Papierkorb (Soft-Delete), zugehörige Kandidaten-Paare auf `merged`. Der
 *  Server prüft selbst, dass ALLE Kontakte Status „Altkunde" haben (Re-Check)
 *  und führt das in einer Transaktion aus (M-SP3a-Endpoint). */
/** Meldung, wenn der Server das Verwerfen eines Altkunden-Clusters mit 403 ablehnt. */
export const DISCARD_ALTKUNDEN_FORBIDDEN_MESSAGE =
  "Keine Berechtigung: Altkunden-Cluster dürfen nur Admin, GF/GL und freigegebene Bereiniger verwerfen.";

export const discardAltkundenCluster = async (
  contactIds: number[],
  reason?: string,
): Promise<{ discarded: number[]; resolvedCandidateIds: number[] }> => {
  const res = await fetch(
    apiUrl("duplicate-candidates/discard-altkunden-cluster"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contactIds, ...(reason ? { reason } : {}) }),
    },
  );
  // 403 = fehlendes Recht (Rolle/Allowlist). Den englischen Server-Text
  // („Missing required role") nicht durchreichen, sondern verständlich melden.
  if (res.status === 403)
    throw new Error(DISCARD_ALTKUNDEN_FORBIDDEN_MESSAGE);
  if (!res.ok)
    throw new Error(
      await readErrorMessage(
        res,
        `Altkunden-Cluster verwerfen fehlgeschlagen: HTTP ${res.status}`,
      ),
    );
  return res.json();
};

/** Acquire a soft lock on a cluster (its contacts) while reviewing/merging. */
export const lockDuplicateCluster = async (
  contactIds: number[],
): Promise<void> => {
  const res = await fetch(apiUrl("duplicate-candidates/lock"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactIds }),
  });
  if (!res.ok)
    throw new Error(
      await readErrorMessage(res, `Sperren fehlgeschlagen: HTTP ${res.status}`),
    );
};

/** Release the user's lock on a cluster (best-effort). */
export const unlockDuplicateCluster = async (
  contactIds: number[],
): Promise<void> => {
  try {
    await fetch(apiUrl("duplicate-candidates/unlock"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contactIds }),
    });
  } catch {
    /* best-effort */
  }
};

/** Batch attached-record counts + Ansprechpartner links for contacts. */
export const fetchRelatedSummary = async (
  ids: number[],
): Promise<Record<number, RelatedSummary>> => {
  if (ids.length === 0) return {};
  const { data } = await apiFetch<Record<number, RelatedSummary>>(
    "duplicate-candidates/related-summary",
    { params: { ids: ids.join(",") } },
  );
  return data ?? {};
};

/** Full attached-record lists for one contact (lazy, on expand). */
export const fetchRelatedDetail = async (
  contactId: number,
): Promise<RelatedDetail> => {
  const { data } = await apiFetch<RelatedDetail>(
    `duplicate-candidates/${contactId}/related-detail`,
  );
  return data;
};

/**
 * Papierkorb: restore a hard-deleted merge loser from its snapshot (master data
 * only). Returns the id of the freshly re-created contact.
 */
export const restoreMergeLoser = async (
  mergeHistoryId: number,
): Promise<{ restoredContactId: number }> => {
  const res = await fetch(
    apiUrl(`duplicate-candidates/merge-history/${mergeHistoryId}/restore`),
    { method: "POST" },
  );
  if (!res.ok)
    throw new Error(
      await readErrorMessage(
        res,
        `Wiederherstellen fehlgeschlagen: HTTP ${res.status}`,
      ),
    );
  return res.json();
};

/** Papierkorb: permanently delete a merge loser's snapshot. */
export const purgeMergeLoser = async (
  mergeHistoryId: number,
): Promise<void> => {
  const res = await fetch(
    apiUrl(`duplicate-candidates/merge-history/${mergeHistoryId}`),
    { method: "DELETE" },
  );
  if (!res.ok)
    throw new Error(
      await readErrorMessage(
        res,
        `Endgültig löschen fehlgeschlagen: HTTP ${res.status}`,
      ),
    );
};
